import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowUpRightIcon,
  ArrowPathIcon,
  CreditCardIcon,
} from '@heroicons/react/24/outline';
import { licenseAPI } from '../../services/api';
import { useTenantPath } from '../../hooks/useTenantPath';

// Compact Plan & Usage panel — reusable card that shows the current tenant's
// tier + usage bars against tier limits. Used inline on the Impact page and
// as the body of the dedicated PlanUsagePage. Backend contract: see
// license.py::get_tenant_usage — the `rows` array is rendered 1:1.

// Thresholds mirror usage_service.ALERT_THRESHOLDS (75/90/100) exactly. They
// used to be 70/90 here, so the panel warned at a point the backend did not
// recognise and stayed silent through the whole grace band.
const barColor = (pct) => {
  if (pct >= 100) return 'bg-red-600';   // into the grace allowance
  if (pct >= 90) return 'bg-red-500';
  if (pct >= 75) return 'bg-amber-500';
  return 'bg-indigo-500';
};

const UsageRow = ({ row, answerAllowance }) => {
  const isAnswerRow = row.key === 'ai_answers';
  const pct = isAnswerRow && Number.isFinite(answerAllowance?.percent)
    ? answerAllowance.percent
    : (row.pct || 0);
  const bar = barColor(pct);
  // The answer bar represents allowance + the 10% grace band. At 100% of the
  // purchased allowance the main segment is full and the final 1/11 remains
  // visibly available for grace. Other quota rows have no grace segment.
  const baseWidth = isAnswerRow
    ? Math.min(pct, 100) / 1.1
    : Math.min(pct, 100);
  const graceWidth = isAnswerRow
    ? Math.min(Math.max(pct - 100, 0), 10) / 1.1
    : 0;
  return (
    <div className="py-2.5 flex items-center gap-3">
      <div className="w-40 sm:w-52 text-sm text-gray-700 dark:text-gray-300">
        <div className="truncate">{row.label}</div>
        {row.sublabel && (
          <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
            ({row.sublabel})
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden flex">
          {/* Filled portion, capped at the allowance. */}
          <div
            className={`h-full ${bar} transition-all`}
            style={{ width: `${baseWidth}%` }}
          />
          {/* Grace consumption, drawn beyond the allowance rather than clamped
              away. Scaled against the 10% grace band, so a full second segment
              means grace is spent and the agent is about to stop. */}
          {isAnswerRow && graceWidth > 0 && (
            <div
              className="h-full bg-red-800 transition-all"
              style={{ width: `${graceWidth}%` }}
              title="Using grace allowance"
            />
          )}
        </div>
      </div>
      <div className="w-40 text-right text-sm text-gray-700 dark:text-gray-300 tabular-nums whitespace-nowrap">
        <span className="font-semibold text-gray-900 dark:text-white">{row.display_used}</span>
        <span className="text-gray-500 dark:text-gray-400"> / {row.display_limit}</span>
        <span className="text-gray-400 dark:text-gray-500 text-xs ml-1">({pct}%)</span>
      </div>
    </div>
  );
};

const tierColor = (tier) => {
  const key = String(tier || '').toLowerCase();
  if (key === 'scale') return 'text-purple-700 bg-purple-100 dark:bg-purple-900/40 dark:text-purple-200';
  if (key === 'growth') return 'text-indigo-700 bg-indigo-100 dark:bg-indigo-900/40 dark:text-indigo-200';
  if (key === 'starter') return 'text-sky-700 bg-sky-100 dark:bg-sky-900/40 dark:text-sky-200';
  return 'text-gray-700 bg-gray-100 dark:bg-gray-700 dark:text-gray-200';
};

const upgradeTargetForTier = (tier) => {
  const key = String(tier || '').toLowerCase();
  if (key === 'starter') return 'Upgrade to Growth';
  if (key === 'growth') return 'Upgrade to Scale';
  if (key === 'scale') return 'Manage plan';
  return 'Upgrade plan';
};

const PlanUsagePanel = ({ compact = false, showTitle = true }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const tenantPath = useTenantPath();

  const load = () => {
    setLoading(true);
    setError(null);
    licenseAPI
      .getUsage()
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || e.message || 'Could not load usage.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  if (loading && !data) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5 text-sm text-gray-500 dark:text-gray-400">
        Loading plan usage…
      </div>
    );
  }
  if (error && !data) {
    return (
      <div className="bg-red-50 dark:bg-red-900/30 rounded-lg border border-red-200 dark:border-red-800 p-4 text-sm text-red-800 dark:text-red-200 flex items-center justify-between gap-3">
        <span>{error}</span>
        <button
          onClick={load}
          className="text-xs font-medium underline hover:no-underline"
          type="button"
        >
          Retry
        </button>
      </div>
    );
  }
  if (!data) return null;

  // The AI-answer allowance state is computed server-side by
  // usage_service.usage_state, which is also what gates the agent. Trust it
  // rather than recomputing: a second copy of the thresholds is exactly how
  // this panel and the enforcement came to disagree.
  const answerState = data.answer_allowance?.state;
  const inGrace = answerState === 'over';
  const exhausted = answerState === 'exhausted';

  const nearLimit =
    answerState === 'warning' || (data.rows || []).some((r) => r.pct >= 75);
  const overLimit =
    answerState === 'critical' || inGrace || exhausted ||
    (data.rows || []).some((r) => r.pct >= 90);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
      {showTitle && (
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide flex items-center gap-2">
              <CreditCardIcon className="w-4 h-4" />
              Plan & Usage
            </h2>
            <div className="mt-1 flex items-center gap-2 flex-wrap">
              <span
                className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${tierColor(
                  data.tier
                )}`}
              >
                {data.tier_label || data.tier}
              </span>
              {data.trial_days_left !== null && data.trial_days_left !== undefined && data.subscription_status === 'trialing' && (
                <span className="text-xs text-amber-700 dark:text-amber-300 font-medium">
                  Trial ends in {data.trial_days_left} {data.trial_days_left === 1 ? 'day' : 'days'}
                </span>
              )}
              {data.subscription_status && !['active', 'active_paid', 'trialing'].includes(data.subscription_status) && (
                <span className="text-xs px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200">
                  {data.subscription_status}
                </span>
              )}
            </div>
          </div>
          <button
            onClick={load}
            type="button"
            title="Reload usage"
            className="p-1.5 text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-white"
          >
            <ArrowPathIcon className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="divide-y divide-gray-100 dark:divide-gray-700">
        {(data.rows || []).map((row) => (
          <UsageRow
            key={row.key}
            row={row}
            answerAllowance={data.answer_allowance}
          />
        ))}
      </div>

      {(nearLimit || overLimit || (data.trial_days_left !== null && data.subscription_status === 'trialing')) && (
        <div className="mt-4 pt-3 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between gap-3">
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {exhausted
              ? 'AI answers are used up, including the grace allowance — the assistant has stopped answering. Upgrade to switch it back on.'
              : inGrace
              ? "You've used this cycle's AI answers and are into the grace allowance — the assistant is still answering, but not for long."
              : overLimit
              ? "You're at the limit on one or more items — upgrade to keep growing."
              : nearLimit
              ? "You're approaching a limit — plan an upgrade before it caps out."
              : data.trial_days_left !== null && data.subscription_status === 'trialing'
              ? 'Trial in progress — upgrade any time to keep everything you set up.'
              : ''}
          </div>
          <Link
            to={tenantPath('/billing')}
            className="inline-flex items-center gap-1 text-xs font-semibold text-indigo-600 dark:text-indigo-400 hover:underline whitespace-nowrap"
          >
            {upgradeTargetForTier(data.tier)}
            <ArrowUpRightIcon className="w-3 h-3" />
          </Link>
        </div>
      )}

      {compact && (
        <div className="mt-3 text-right">
          <Link
            to={tenantPath('/plan-usage')}
            className="text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:underline"
          >
            View full plan details →
          </Link>
        </div>
      )}
    </div>
  );
};

export default PlanUsagePanel;
